import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react'

export type LogTag = 'sys' | 'info' | 'ok' | 'err' | 'user' | 'agent'

export type LogLine = {
  id: number
  ts: string
  tag: LogTag
  msg: string
  partial?: boolean
}

type TalkLogValue = {
  lines: LogLine[]
  log: (tag: LogTag, msg: string, opts?: { partial?: boolean }) => number
  updateLine: (id: number, msg: string, partial: boolean) => void
  clear: () => void
}

const TalkLogContext = createContext<TalkLogValue | null>(null)

function nowTs(): string {
  const d = new Date()
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.${d.getMilliseconds().toString().padStart(3, '0')}`
}

export function TalkLogProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<LogLine[]>([])
  const lineId = useRef(0)

  const log = useCallback((tag: LogTag, msg: string, opts: { partial?: boolean } = {}) => {
    const id = ++lineId.current
    setLines((prev) => [...prev, { id, ts: nowTs(), tag, msg, partial: opts.partial }])
    return id
  }, [])

  const updateLine = useCallback((id: number, msg: string, partial: boolean) => {
    setLines((prev) => prev.map((line) => (line.id === id ? { ...line, msg, partial } : line)))
  }, [])

  const clear = useCallback(() => setLines([]), [])

  const value = useMemo(() => ({ lines, log, updateLine, clear }), [lines, log, updateLine, clear])
  return <TalkLogContext.Provider value={value}>{children}</TalkLogContext.Provider>
}

export function useTalkLog(): TalkLogValue {
  const value = useContext(TalkLogContext)
  if (!value) throw new Error('useTalkLog must be used inside TalkLogProvider')
  return value
}
